// src/components/MapMarkerLayer.tsx
import type { MarkerData } from "@/contexts/MapMarkersContext"
import { useMapMarkerData } from "@/hooks/useMapMarkerData"
import { useCallback } from "react"

// LocationData 타입을 내보내기
export type LocationData = {
  id: string
  name: string
  latitude: number
  longitude: number
  type: "노지" | "주차장" | "캠핑장" | "설비" | "기타"
  icon?: string
  imageUrl?: string
  isPending?: boolean
}

type MapMarkerLayerProps = {
  locations: LocationData[]
  onMarkerClick?: (location: LocationData) => void
}

export const MapMarkerLayer = ({
  locations,
  onMarkerClick,
}: MapMarkerLayerProps) => {
  // 데이터를 마커로 변환하는 함수를 메모이제이션
  const getMarkerFromLocation = useCallback(
    (location: LocationData): MarkerData => {
      // API의 icon 문자열을 PNG 아이콘 경로로 매핑
      const getIconUrlFromIconName = (iconName: string | undefined) => {
        if (!iconName) return null

        const iconMap: Record<string, string> = {
          caravan: "/icons/markers/default.png",
          camping: "/icons/markers/camping.png",
          field: "/icons/markers/field.png",
          parking: "/icons/markers/parking.png",
          facility: "/icons/markers/gear.png", // facility -> gear.png
        }

        return iconMap[iconName.toLowerCase()] || null
      }

      // 마커 아이콘 타입에 따라 PNG 아이콘 경로 사용 (icon 필드가 없을 때의 폴백)
      const getIconUrlForType = (type: LocationData["type"]) => {
        switch (type) {
          case "노지":
            return "/icons/markers/field.png"
          case "주차장":
            return "/icons/markers/parking.png"
          case "캠핑장":
            return "/icons/markers/camping.png"
          case "설비":
            return "/icons/markers/gear.png"
          default:
            return "/icons/markers/default.png"
        }
      }

      const baseIconUrl = location.isPending
        ? getIconUrlFromIconName(location.icon) || getIconUrlForType(location.type)
        : getIconUrlFromIconName(location.icon) ||
          location.imageUrl ||
          getIconUrlForType(location.type)
      let htmlContent: string | undefined
      if (location.isPending && baseIconUrl) {
        htmlContent = `
          <div style="position: relative; width: 48px; height: 52px; display: flex; justify-content: center; align-items: center;">
            <img src="${baseIconUrl}" style="width: 100%; height: 100%; object-fit: contain;" />
            <div style="
              position: absolute;
              top: -6px;
              right: -6px;
              background-color: #ff4d4f;
              color: white;
              border-radius: 50%;
              width: 22px;
              height: 22px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
              font-size: 13px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
              border: 2px solid white;
              z-index: 10;
            ">?</div>
          </div>
        `
      }

      return {
        id: location.id,
        lat: location.latitude,
        lng: location.longitude,
        title: location.name,
        // 우선순위: API icon > imageUrl > siteType 기반 아이콘
        iconUrl: !htmlContent ? baseIconUrl : undefined,
        htmlContent,
        clickable: true,
        onClick: () => onMarkerClick?.(location),
        // 원본 데이터 저장
        originalData: location,
      }
    },
    [onMarkerClick],
  )

  // 훅을 사용하여 마커 관리
  useMapMarkerData({
    data: locations,
    getMarkerData: getMarkerFromLocation,
    deps: [onMarkerClick], // 클릭 핸들러가 변경되면 마커 업데이트
  })

  // 이 컴포넌트는 UI를 직접 렌더링하지 않음
  return null
}
